import { useCallback, useEffect } from 'react';

/**
 * Hook to handle keyboard shortcuts while ignoring input fields
 * @param {string|string[]} key - The key(s) to listen for (case-insensitive)
 * @param {Function} callback - Function to call when key is pressed
 * @param {Object} options - Optional configuration
 * @param {boolean} options.disabled - Whether the shortcut is disabled
 */
const useKeyboardShortcut = (key, callback, options = {}) => {
    const { disabled = false } = options;

    const keys = Array.isArray(key) ? key.map(k => k.toLowerCase()) : [key.toLowerCase()];

    const handleKeyDown = useCallback((e) => {
        // Ignore if typing in an input field
        if (
            e.target.tagName === 'INPUT' ||
            e.target.tagName === 'TEXTAREA' ||
            e.target.isContentEditable
        ) {
            return;
        }

        if (keys.includes(e.key.toLowerCase())) {
            e.preventDefault();
            callback(e);
        }
    }, [keys, callback]);

    useEffect(() => {
        if (disabled) return;

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown, disabled]);
};

export default useKeyboardShortcut;
